import { BillingSettlementStatus, BillingSettlementType, InvoiceDTO, ReportFileDTO } from '@/models/back-office-pages/invoices'
import {
  LoanBookFullReturnStatus,
  LoanBookType,
  LoanBookVatNoteInvoiceType,
  LoanBookVatNoteStatus,
} from '@/models/back-office-pages/loan-book'
import { NoteDTO, NoteType, PayoutProductType } from '@/models/back-office-pages/notes'
import { FundingLine } from '@/models/back-office-pages/prospects'
import {
  AdministrationTypes,
  AutoCommentLanguageList,
  Currency,
  FromToFilterInput,
  MinMaxFilterInput,
  SourcingAgentTypes,
} from '@/models/common'
import { Direction } from '@/models/table'

export enum BankStatementFilterStatus {
  ALL = 'ALL',
  ARCHIVED = 'ARCHIVED',
  CLOSED = 'CLOSED',
  OPEN = 'OPEN',
}

export enum BankStatementStatus {
  CORRUPTED = 'CORRUPTED',
  ARCHIVED = 'ARCHIVED',
  CLOSED = 'CLOSED',
  OPEN = 'OPEN',
  PENDING = 'PENDING',
}

export enum ReconciliationProductType {
  FACTORING = 'FACTORING',
  INVESTMENTS = 'INVESTMENTS',
  LOAN_BOOK = 'LOAN_BOOK',
}

export interface BankStatementDTO {
  productType: ReconciliationProductType
  currency: Currency
  bankName: string
  createdBy: string
  createdByEmail: string
  createdById: string
  createdOn: Date
  endPeriod: Date
  fileName: string
  iban: string
  id: string
  openTransactionsCount: number
  openTransactionsSum: number
  resolvedTransactionsCount: number
  resolvedTransactionsSum: number
  sourcingAgentAdministrates: string
  sourcingAgentName: string
  startPeriod: Date
  status: BankStatementStatus
  totalTransactionsCount: number
  totalTransactionsSum: number
  writeOffCount: number
  writeOffSum: number
}

export interface Page_BankStatementDTO {
  content: Array<BankStatementDTO>
  number: number
  size: number
  totalElements: number
  totalPages: number
}

export interface BankStatementsData {
  bankStatementsList: Page_BankStatementDTO
}

export interface BankStatementsVariables {
  sortDirection?: Direction
  sortField?: string
  pageSize: number
  bankName?: string
  startPeriod?: string
  endPeriod?: string
  pageNum: number
  sourcingAgentCompanyName?: string
  status: BankStatementFilterStatus
}

export interface BankStatementFileDTO {
  content: string
  fileName: string
  size: number
}

export interface DownloadBankStatementData {
  downloadBankStatement: BankStatementFileDTO
}

export interface DownloadBankStatementVariables {
  id: string
}

export interface BankStatementFileDTOInput {
  content: string
  fileName: string
  size: number
}

export interface UploadBankStatementData {
  uploadBankStatement: string
}

export interface UploadBankStatementVariables {
  bankAccountId: string
  bankStatement: BankStatementFileDTOInput
}

export enum BankStatementFieldNames {
  ACCOUNT_ID = 'bankAccountId',
  FILE = 'bankStatement',
}

export interface CreateBankTransactionData {
  createBankTransaction: boolean
}

export interface CreateBankTransactionVariables {
  bankAccountId: string
  senderCompany: string
  senderIban: string
  transactionReference: string
  transactionDate: string
  amount: number
}

export enum CreateBankTransactionFieldNames {
  ACCOUNT_ID = 'bankAccountId',
  COMPANY = 'senderCompany',
  IBAN = 'senderIban',
  TRANSACTION_REFERENCE = 'transactionReference',
  TRANSACTION_DATE = 'transactionDate',
  AMOUNT = 'amount',
}

export interface BankAccountDTO {
  bankName: string
  iban: string
  sourcingAgentAdministrates: string
  sourcingAgentName: string
  id: string
  currency: Currency
  fundingLineTypes: Array<FundingLine>
}

export interface BankAccountsData {
  bankAccountList: Array<BankAccountDTO>
}

export interface BankAccountsVariables {
  productType: PayoutProductType
}

export interface ArchiveBankStatementData {
  archiveBankStatement: BankStatementDTO
}

export interface ArchiveBankStatementVariables {
  id: string
}

export interface BankStatementTotalsDTO {
  openTransactionsSum: number
  resolvedTransactionsSum: number
  totalTransactionsSum: number
  writeOffSum: number
}

export enum PaymentType {
  ICDT = 'ICDT',
  RCDT = 'RCDT',
  RRCT = 'RRCT',
}

export enum BankStatementTransactionStatus {
  CLOSED = 'CLOSED',
  OPEN = 'OPEN',
  CORRUPTED = 'CORRUPTED',
  DUPLICATE = 'DUPLICATE',
}

export interface BankStatementDetailsDTO {
  bankStatementTotals: BankStatementTotalsDTO
  fileName: string
  id: string
  status: BankStatementStatus
  currency: Currency
}

export interface BankStatementDetailsData {
  bankStatementsDetails: BankStatementDetailsDTO
}

export interface BankStatementDetailsVariables {
  bankStatementId: string
}

export enum TransactionType {
  INCOMING = 'INCOMING',
  PAYOUT = 'PAYOUT',
}

export interface BankStatementTransactionDTO {
  bankReference: string
  companyIban: string
  companyName: string
  companyRegistrationNumber: string
  id: string
  openAmount: number
  paymentAmount: number
  paymentDate: Date
  paymentType: PaymentType
  reconciledAmount: number
  status: BankStatementTransactionStatus
  writtenOffAmount: number
  transactionType: TransactionType
  transactionSequence: number
  glAccounts: string
  nextOpenTrxId: string
  previousOpenTrxId: string
  currency: Currency
}

export interface BankStatementTransactionTotalsDTO {
  openAmount: number
  paymentAmount: number
  reconciledAmount: number
  writtenOffAmount: number
}

export interface PageWithTotals_BankStatementTransactionDTO_BankStatementTransactionTotalsDTO {
  content: Array<BankStatementTransactionDTO>
  totals: BankStatementTransactionTotalsDTO
  number: number
  size: number
  totalElements: number
  totalPages: number
}

export interface TransactionsData {
  transactionList: PageWithTotals_BankStatementTransactionDTO_BankStatementTransactionTotalsDTO
}

export interface TransactionsVariables {
  transactionType: TransactionType
  transactionSequence?: number
  sortDirection?: Direction
  companyName?: string
  sortField?: string
  pageSize: number
  pageNum: number
  companyIban?: string
  bankStatementId: string
  glAccounts?: string
}

export enum WriteOffTransactionFieldsNames {
  TRANSACTION_ID = 'bankStatementTransactionId',
  WRITE_OFF_AMOUNT = 'amount',
  RECONCILIATION_ACTION = 'reconciliationAction',
  OPEN_AMOUNT = 'openAmount',
  COMMENT = 'comment',
}

export interface WriteOffTransactionData {
  writeOffTransaction: BankStatementTransactionDTO
}

export interface WriteOffTransactionVariables {
  amount: number
  reconciliationAction: ReconciliationAction
  comment: string
  bankStatementTransactionId: string
}

export interface WriteOffTransactionFormData {
  reconciliationAction: ReconciliationAction
  openAmount: number
  amount: number
  comment: string
  bankStatementTransactionId: string
}

export interface ReOpenTransactionData {
  reOpenDuplicateTransaction: BankStatementTransactionDTO
}

export interface ReOpenTransactionVariables {
  bankStatementTransactionId: string
}

export interface DeleteDuplicateTransactionData {
  deleteDuplicateTransaction: BankStatementTransactionDTO
}

export interface DeleteDuplicateTransactionVariables {
  bankStatementTransactionId: string
}

export interface ReconcileTransactionData {
  reconcileTransaction: BankStatementTransactionDTO
}

export enum CompanyType {
  CLIENT = 'CLIENT',
  DEBTOR = 'DEBTOR',
  INSURANCE = 'INSURANCE',
  SOURCING_AGENT = 'SOURCING_AGENT',
}

export enum InvestigationCompanyType {
  CLIENT = 'CLIENT',
  DEBTOR = 'DEBTOR',
}

export enum ReconciliationAction {
  BANK_FEES = 'BANK_FEES',
  BELOW_1 = 'BELOW_1',
  CREDIT_INVOICE = 'CREDIT_INVOICE',
  CREDIT_NOTE = 'CREDIT_NOTE',
  IDENTIFICATION_PAYMENT = 'IDENTIFICATION_PAYMENT',
  INTERNAL_PAYMENT_INCOMING = 'INTERNAL_PAYMENT_INCOMING',
  INTERNAL_PAYMENT_OUTGOING = 'INTERNAL_PAYMENT_OUTGOING',
  INVESTIGATION = 'INVESTIGATION',
  INVESTIGATION_OUTGOING = 'INVESTIGATION_OUTGOING',
  NOT_PURCHASED_INVOICES = 'NOT_PURCHASED_INVOICES',
  PURCHASE_NOTE = 'PURCHASE_NOTE',
  RECEIVABLES = 'RECEIVABLES',
  INCOMING_OTHER_PRODUCTS = 'INCOMING_OTHER_PRODUCTS',
  OUTGOING_OTHER_PRODUCTS = 'OUTGOING_OTHER_PRODUCTS',
  FACTRIS_FINANCE_TRANSACTION_INCOMING = 'FACTRIS_FINANCE_TRANSACTION_INCOMING',
  FACTRIS_HOLDING_TRANSACTION_INCOMING = 'FACTRIS_HOLDING_TRANSACTION_INCOMING',
  OPERATIONAL_COSTS_INCOMING = 'OPERATIONAL_COSTS_INCOMING',
  FACTRIS_FINANCE_TRANSACTION_OUTGOING = 'FACTRIS_FINANCE_TRANSACTION_OUTGOING',
  FACTRIS_HOLDING_TRANSACTION_OUTGOING = 'FACTRIS_HOLDING_TRANSACTION_OUTGOING',
  OPERATIONAL_COSTS_OUTGOING = 'OPERATIONAL_COSTS_OUTGOING',
  ADDITIONAL_FEES_OUTGOING = 'ADDITIONAL_FEES_OUTGOING',
  ADDITIONAL_FEES_INCOMING = 'ADDITIONAL_FEES_INCOMING',
  RECEIVABLES_TWINFIELD = 'RECEIVABLES_TWINFIELD',
  PAYABLES_TWINFIELD = 'PAYABLES_TWINFIELD',
  INVOICE_WRITE_OFF = 'INVOICE_WRITE_OFF',
  RETURN_TO_CLIENT_LOAN_BOOK = 'RETURN_TO_CLIENT_LOAN_BOOK',
  RECEIVABLES_LOAN_BOOK = 'RECEIVABLES_LOAN_BOOK',
  INVESTIGATION_LOAN_BOOK = 'INVESTIGATION_LOAN_BOOK',
  INCOMING_WRONG_FUNDING_LINE = 'INCOMING_WRONG_FUNDING_LINE',
  OUTGOING_WRONG_FUNDING_LINE = 'OUTGOING_WRONG_FUNDING_LINE',
  RETURN_TO_SENDER_INVESTMENT = 'RETURN_TO_SENDER_INVESTMENT',
  RECEIVABLES_INVESTMENT = 'RECEIVABLES_INVESTMENT',
  INVESTIGATION_INVESTMENT = 'INVESTIGATION_INVESTMENT',
}

export interface ReconcileInvoiceDTOInput {
  invoiceId: string
  reconcileAmount: number
}

export interface ReconcileNoteDTOInput {
  noteId: string
  noteType: NoteType
  reconcileAmount: number
}

export interface ReconcileBillingSettlementDTOInput {
  invoiceId: string
  reconcileAmount: number
  settlementId: string
}

export interface ReconcileVatInvoiceDTOInput {
  reconcileAmount: number
  vatNoteId: string
  outstandingPrincipalToDiscount?: number
  outstandingInterestToDiscount?: number
  outstandingIncreasedFeeToDiscount?: number
}

export interface ReconcileFullReturnDTOInput {
  fullReturnId: string
  reconcileAmount: number
}

export interface ReconcilePartialReturnDTOInput {
  productId: string
  reconcileAmount: number
}

export enum ReconciliationPaymentReturnTo {
  CLIENT = 'CLIENT',
  DEBTOR = 'DEBTOR',
  SENDER = 'SENDER',
  FACTRIS = 'FACTRIS',
}

export enum ReconciliationPaymentTo {
  CLIENT = 'CLIENT',
  DEBTOR = 'DEBTOR',
  SENDER = 'SENDER',
  FACTRIS = 'FACTRIS',
  INSURANCE = 'INSURANCE',
}

export enum ReconciliationInvestigationReason {
  INVOICE_VERIFICATION = 'INVOICE_VERIFICATION',
  REMITTANCE_REQUIRED = 'REMITTANCE_REQUIRED',
  CREDIT_NOT_DEDUCTED_BY_DEBTOR = 'CREDIT_NOT_DEDUCTED_BY_DEBTOR',
  INVESTIGATION_GENERAL = 'INVESTIGATION_GENERAL',
  OVERDUE_INVOICE = 'OVERDUE_INVOICE',
  DIFFERENT_REASON = 'DIFFERENT_REASON',
}

export enum ReconciliationInvestigationReasonEmailRequired {
  INVOICE_VERIFICATION = 'INVOICE_VERIFICATION',
  REMITTANCE_REQUIRED = 'REMITTANCE_REQUIRED',
}

export interface ReconcileTransactionVariables {
  bankStatementTransactionId: string
  companyId?: string
  paymentReceivedFrom?: CompanyType
  reconciliationAction: ReconciliationAction
  reconciliationAmount: number
  invoices?: Array<ReconcileInvoiceDTOInput>
  notes?: Array<ReconcileNoteDTOInput>
  vatInvoices?: Array<ReconcileVatInvoiceDTOInput>
  vatSettlements?: Array<ReconcileVatInvoiceDTOInput>
  fullReturns?: Array<ReconcileFullReturnDTOInput>
  partialReturns?: Array<ReconcilePartialReturnDTOInput>
  comments?: string
  sourcingAgentAdministrates?: SourcingAgentTypes
  billingSettlements?: Array<ReconcileBillingSettlementDTOInput>
  returnTo?: ReconciliationPaymentReturnTo
  returnToIban?: string
  returnToCompanyName?: string
  settlementContractId?: string
  returnPaymentDetails?: string
  returnReason?: string
  returnSettlementAmount?: number
  returnSettlementVat?: number
  returnSettlementDescription?: string
  addSettlementForReturn?: boolean
  companyName?: string
  autoComments?: string
  investorId?: string
  investigationReason?: ReconciliationInvestigationReason
  investigationEmailTo?: InvestigationCompanyType
  investigationEmailLanguage?: string
  investigationEmailRecipients?: string[]
  investigationInvoiceNumbers?: string
  investigationCompanyId?: string
  riskMarkTransactionSuspendedByBank?: Boolean
  riskMarkTransactionMadeByThirdParty?: Boolean
}

export interface InvoiceForReconciliationDTO extends InvoiceDTO {
  selected: boolean
  reconcileAmount: number
  negativeWriteOff: number
}

export interface CreditNoteForReconciliationDTO extends NoteDTO {
  selected: boolean
  reconcileAmount: number
}

export interface BillingSettlementsForReconciliationDTO extends ReconcileBillingSettlementDTO {
  selected: boolean
  reconcileAmount: number
}

export enum RepaymentForFactoring {
  INVOICES = 'INVOICES',
  BILLING_SETTLEMENTS = 'BILLING_SETTLEMENTS',
}

export enum RepaymentForLoanBook {
  VAT_INVOICES = 'VAT_INVOICES',
  ADDITIONAL_FEES = 'ADDITIONAL_FEES',
  PARTIAL_RETURN = 'PARTIAL_RETURN',
  FULL_RETURN = 'FULL_RETURN',
}

export enum ReconciliationProduct {
  FACTORING = 'FACTORING',
  LOAN_BOOK = 'LOAN_BOOK',
}

export interface ReconcileTransactionFormData {
  bankStatementTransactionId: string
  paymentReceivedFrom: CompanyType | null
  companyId: string
  companyName: string
  clientId: string
  reconciliationAction: ReconciliationAction
  repaymentFor: RepaymentForFactoring | RepaymentForLoanBook
  invoices?: Array<InvoiceForReconciliationDTO>
  notes?: Array<CreditNoteForReconciliationDTO>
  billingSettlements?: Array<BillingSettlementsForReconciliationDTO>
  vatInvoices?: Array<ExtendedReconciliationVatInvoice>
  additionalFees?: Array<ExtendedReconciliationVatInvoice>
  partialReturn?: Array<ExtendedReconciliationPartialReturn>
  fullReturn?: Array<ExtendedReconciliationFullReturn>
  reconciliationAmount: number
  amountForAutomaticPrefill: number
  comments: string
  transactionType?: TransactionType
  sourcingAgentAdministrates?: SourcingAgentTypes
  loanBookProduct?: LoanBookType
  reconciliationProduct: ReconciliationProductType
  remainingReconciliationAmount: number
  totalAmountToReconcile: number
  maxReconciliationAmount: number
  returnTo: ReconciliationPaymentReturnTo | null
  returnToCompanyName?: string
  returnToIban: string
  settlementContractId: string
  returnPaymentDetails: string
  returnReason: string[]
  returnSettlementAmount: number
  returnSettlementVat: number
  returnSettlementDescription: string | null
  addSettlementForReturn: boolean
  autoComments: string
  useAutoComment: boolean
  companyForAutoComment: string
  languageForAutoComment: AutoCommentLanguageList
  investigationReason: ReconciliationInvestigationReason
  investigationEmailTo: InvestigationCompanyType
  investigationEmailLanguage: string
  investigationEmailRecipients: string[]
  investigationInvoiceNumbers: string
  investigationCompanyId: string
  sentInvestigationAutoEmail: boolean
  fillInvestigationAutoComment: boolean
  riskMarkTransactionSuspendedByBank: boolean
  riskMarkTransactionMadeByThirdParty: boolean
}

export interface ReconcileInvestorTransactionFormData {
  reconciliationAction: ReconciliationAction
  comments?: string
  returnPaymentDetails?: string
  investorId?: string
}

export enum ReconcileTransactionFieldsNames {
  LOAN_BOOK_PRODUCT = 'loanBookProduct',
  RECONCILIATION_PRODUCT = 'reconciliationProduct',
  TRANSACTION_ID = 'bankStatementTransactionId',
  PAYMENT_RECEIVED_FROM = 'paymentReceivedFrom',
  COMPANY_ID = 'companyId',
  CLIENT_ID = 'clientId',
  INVESTOR_ID = 'investorId',
  COMPANY_NAME = 'companyName',
  RECONCILIATION_ACTION = 'reconciliationAction',
  REPAYMENT_FOR = 'repaymentFor',
  INVOICES = 'invoices',
  NOTES = 'notes',
  VAT_INVOICES = 'vatInvoices',
  ADDITIONAL_FEES = 'additionalFees',
  PARTIAL_RETURN = 'partialReturn',
  FULL_RETURN = 'fullReturn',
  BILLING_SETTLEMENTS = 'billingSettlements',
  RECONCILIATION_AMOUNT = 'reconciliationAmount',
  AMOUNT_FOR_AUTOMATIC_PREFILL = 'amountForAutomaticPrefill',
  REMAINING_RECONCILIATION_AMOUNT = 'remainingReconciliationAmount',
  RECONCILIATION_MAX_AMOUNT = 'maxReconciliationAmount',
  TOTAL_AMOUNT_TO_RECONCILE = 'totalAmountToReconcile',
  COMMENT = 'comments',
  TRANSACTION_TYPE = 'transactionType',
  SOURCING_AGENT_ADMINISTRATES = 'sourcingAgentAdministrates',
  RETURN_TO = 'returnTo',
  RETURN_TO_IBAN = 'returnToIban',
  RETURN_TO_COMPANY_NAME = 'returnToCompanyName',
  RETURN_PAYMENT_DETAILS = 'returnPaymentDetails',
  RETURN_REASON = 'returnReason',
  ADD_SETTLEMENT_FOR_CLIENT = 'addSettlementForReturn',
  SETTLEMENT_CONTRACT_ID = 'settlementContractId',
  RETURN_SETTLEMENT_AMOUNT = 'returnSettlementAmount',
  RETURN_SETTLEMENT_VAT = 'returnSettlementVat',
  RETURN_SETTLEMENT_DESCRIPTION = 'returnSettlementDescription',
  USE_AUTO_COMMENT = 'useAutoComment',
  COMPANY_FOR_AUTO_COMMENT = 'companyForAutoComment',
  LANGUAGE_FOR_AUTO_COMMENT = 'languageForAutoComment',
  AUTO_COMMENT = 'autoComments',
  INVESTIGATION_REASON = 'investigationReason',
  SENT_INVESTIGATION_AUTO_EMAIL = 'sentInvestigationAutoEmail',
  FILL_INVESTIGATION_AUTO_COMMENT = 'fillInvestigationAutoComment',
  INVESTIGATION_EMAIL_TO = 'investigationEmailTo',
  INVESTIGATION_COMPANY_ID = 'investigationCompanyId',
  INVESTIGATION_EMAIL_RECIPIENTS = 'investigationEmailRecipients',
  INVESTIGATION_EMAIL_LANGUAGE = 'investigationEmailLanguage',
  INVESTIGATION_INVOICE_NUMBERS = 'investigationInvoiceNumbers',
  RISK_MARK_TRANSACTION_SUSPENDED_BY_BANK = 'riskMarkTransactionSuspendedByBank',
  RISK_MARK_TRANSACTION_MADE_BY_THIRD_PARTY = 'riskMarkTransactionMadeByThirdParty',
}

export enum ReconcileTransactionFieldsArrayNames {
  INVOICE_NUMBER = 'invoiceNumber',
  RECONCILIATION_AMOUNT = 'reconcileAmount',
  NEGATIVE_WRITE_OFF = 'negativeWriteOff',
  INVOICE_AMOUNT = 'invoiceAmount',
  FACTOR_AMOUNT = 'factorAmount',
  OPEN_AMOUNT = 'openAmount',
  TOTAL_WITH_VAT = 'totalInclVat',
  STATUS = 'status',
  SELECTED = 'selected',
}

export enum LoanBookReconcileTransactionFieldsArrayNames {
  NOTE = 'vatNoteNumber',
  ISSUE_DATE = 'issueDate',
  RPA_NUMBER = 'rpaNumber',
  PRINCIPAL = 'outstandingPrincipal',
  INTEREST = 'outstandingInterest',
  INCREASED_FEE = 'outstandingIncreasedFee',
  TOTAL_AMOUNT_TO_BE_PAID = 'outstandingAmount',
  OPEN_AMOUNT = 'openAmount',
  RECONCILIATION_AMOUNT = 'reconcileAmount',
  PRINCIPAL_TO_DISCOUNT = 'outstandingPrincipalToDiscount',
  INTEREST_TO_DISCOUNT = 'outstandingInterestToDiscount',
  INCREASED_FEE_DISCOUNT = 'outstandingIncreasedFeeToDiscount',
  REMAINING_AMOUNT_TO_DISCOUNT = 'remainingAmountToDiscount',
  DUE_DATE = 'dueDate',
  STATUS = 'status',
  SELECTED = 'selected',
}

export interface ReconcileActionsData {
  reconcileActions: Array<ReconciliationActionDTO>
}

export interface ReconcileActionsVariables {
  bankStatementTransactionId: string
  sourcingAgentAdministrates?: SourcingAgentTypes
  product: ReconciliationProductType
}

export interface ReconciliationActionDTO {
  glAccount: string
  reconciliationAction: ReconciliationAction
}

export interface ReconciliationInvoicesData {
  reconciliationInvoices: Array<InvoiceDTO>
}

export interface ReconciliationInvoicesVariables {
  companyId?: string
  invoiceNumber?: string
  bankStatementId: string
  sourcingAgentAdministrates: SourcingAgentTypes
}

export interface ReconciliationNotesData {
  reconciliationNotes: Array<NoteDTO>
}

export interface ReconciliationNotesVariables {
  companyId?: string
  noteNumber?: string
  noteType: NoteType
  bankStatementId: string
  sourcingAgentAdministrates: SourcingAgentTypes
}

export interface ReconcileBillingSettlementDTO {
  currency: Currency
  amount: number
  clientCompanyName: string
  createdOn: Date
  description: string
  id: string
  invoiceId: string
  invoiceNumber: string
  openAmount: number
  settlementType: BillingSettlementType
  status: BillingSettlementStatus
}

export interface ReconciliationBillingSettlementsData {
  reconciliationBillingSettlements: Array<ReconcileBillingSettlementDTO>
}

export interface ReconciliationBillingSettlementsVariables {
  companyId: string
  sourcingAgentAdministrates: SourcingAgentTypes
  invoiceNumber?: string
  bankStatementId: string
}

export interface ReconciliationVatInvoiceDTO {
  agreementId: string
  clientId: string
  companyId: string
  companyName: string
  currency: Currency
  daysOverdue: number
  dueDate: Date
  id: string
  invoiceType: LoanBookVatNoteInvoiceType
  issueDate: Date
  outstandingAmount: number
  outstandingIncreasedFee: number
  outstandingInterest: number
  outstandingPrincipal: number
  productType: LoanBookType
  remainingToPayAmount: number
  rpaNumber: string
  status: LoanBookVatNoteStatus
  totalIncreasedFeeInclVat: number
  totalInterestInclVat: number
  totalPrincipal: number
  vatNoteNumber: string
  productNumber: string
}

export interface ExtendedReconciliationVatInvoice extends ReconciliationVatInvoiceDTO {
  reconcileAmount?: number
  outstandingIncreasedFeeToDiscount?: number
  outstandingInterestToDiscount?: number
  outstandingPrincipalToDiscount?: number
  remainingAmountToDiscount?: number
  selected?: boolean
}

export interface ExtendedReconciliationAdditionalFees extends ReconciliationVatInvoiceDTO {
  reconcileAmount?: number
  remainingAmountToDiscount?: number
  selected?: boolean
}

export interface ReconciliationLoanBookAdditionalFeesInvoicesData {
  reconciliationLoanBookAdditionalFeesInvoices: Array<ReconciliationVatInvoiceDTO>
}

export interface ReconciliationLoanBookAdditionalFeesInvoicesVariables {
  companyId: string
  bankStatementId: string
  sourcingAgentAdministrates: SourcingAgentTypes
}

export interface ReconciliationLoanBookVatInvoicesData {
  reconciliationLoanBookVatInvoices: Array<ReconciliationVatInvoiceDTO>
}

export interface ReconciliationLoanBookVatInvoicesVariables {
  companyId: string
  product: LoanBookType
  bankStatementId: string
  sourcingAgentAdministrates: SourcingAgentTypes
}

export interface ReconciliationLoanBookPartialReturnData {
  reconciliationPartialReturn: Array<ReconciliationVatInvoiceDTO>
}

export interface ExtendedReconciliationFullReturn extends ReconciliationFullReturnDTO {
  reconcileAmount?: number
  remainingAmountToDiscount?: number
  selected?: boolean
}

export interface ReconciliationLoanBookFullReturnVariables {
  companyId: string
  bankStatementId: string
  sourcingAgentAdministrates: SourcingAgentTypes
}

export interface ReconciliationFullReturnDTO {
  agreementId: string
  clientId: string
  companyId: string
  companyName: string
  currency: Currency
  dueDate: Date
  fullReturnNoteNumber: string
  id: string
  invoiceType: LoanBookVatNoteInvoiceType
  issueDate: Date
  openAmount: number
  productNumber: string
  productType: LoanBookType
  rpaNumber: string
  status: LoanBookFullReturnStatus
  totalAmount: number
}

export interface ReconciliationLoanBookFullReturnData {
  reconciliationLoanBookFullReturns: Array<ReconciliationFullReturnDTO>
}

export interface ReconciliationPartialReturnDTO {
  agreementId: string
  clientId: string
  companyId: string
  companyName: string
  currency: Currency
  dueDate: Date
  id: string
  issueDate: Date
  openAmount: number
  payoutNumber: string
  productType: LoanBookType
  rpaNumber: string
  totalAmount: number
}

export interface ExtendedReconciliationPartialReturn extends ReconciliationPartialReturnDTO {
  reconcileAmount?: number
  remainingAmountToDiscount?: number
  selected?: boolean
}

export interface ReconciliationLoanBookPartialReturnsData {
  reconciliationLoanBookPartialReturns: Array<ReconciliationPartialReturnDTO>
}

export interface ReconciliationLoanBookPartialReturnsVariables {
  companyId: string
  product: LoanBookType
  bankStatementId: string
  sourcingAgentAdministrates: SourcingAgentTypes
}

export interface ReconcileCompanyDTO {
  companyName: string
  clientId: string
  id: string
}

export enum ClientDebtorCompanyType {
  CLIENT = 'CLIENT',
  DEBTOR = 'DEBTOR',
}

export interface ReconciliationCompaniesData {
  reconciliationCompanies: Array<ReconcileCompanyDTO>
}

export interface ReconciliationCompaniesVariables {
  companyName: string
  bankStatementId: string
  companyType?: ClientDebtorCompanyType
  sourcingAgentAdministrates?: SourcingAgentTypes
  product: ReconciliationProductType
}

export interface ReconciliationInvestigationEmailsData {
  reconciliationInvestigationEmails: string[]
}

export interface ReconciliationInvestigationEmailsVariables {
  investigationCompanyId: string
  investigationEmailTo: InvestigationCompanyType
}

export interface ReconciliationInvestigationCompaniesData {
  reconciliationInvestigationCompanies: Array<ReconcileCompanyDTO>
}

export interface ReconciliationInvestigationCompaniesVariables {
  companyType: InvestigationCompanyType
  companyName?: string
}

export interface WriteOffActionsData {
  writeOffActions: Array<ReconciliationActionDTO>
}

export interface WriteOffActionsVariables {
  bankStatementTransactionId: string
}

export interface ReturnToIbansData {
  reconciliationReturnToIban: Array<string>
}

export interface ReturnToIbansVariables {
  companyId: string
  returnTo: ReconciliationPaymentReturnTo
}

export enum ReconciliationType {
  AUTO_RECONCILED = 'AUTO_RECONCILED',
  AUTO_WRITE_OFF = 'AUTO_WRITE_OFF',
  MANUAL_RECONCILIATION = 'MANUAL_RECONCILIATION',
  MANUAL_WRITE_OFF = 'MANUAL_WRITE_OFF',
}

export enum ReconciliationLogStatus {
  ARCHIVED = 'ARCHIVED',
  OPEN = 'OPEN',
  INVOICE_CLOSED = 'INVOICE_CLOSED',
}

export enum ReconciliationLogProductType {
  CREDIT_LINE = 'CREDIT_LINE',
  FACTORING = 'FACTORING',
  LOANS = 'LOANS',
  REVERSE_FACTORING = 'REVERSE_FACTORING',
  INVESTMENTS = 'INVESTMENTS',
}

export enum RepaymentForType {
  ADDITIONAL_FEES = 'ADDITIONAL_FEES',
  BILLING_SETTLEMENTS = 'BILLING_SETTLEMENTS',
  FULL_RETURN = 'FULL_RETURN',
  INVOICES = 'INVOICES',
  VAT_INVOICES = 'VAT_INVOICES',
  PARTIAL_RETURN = 'PARTIAL_RETURN',
}

export enum ReconciliationPaymentReturnStatus {
  BLOCKED = 'blocked',
  NEW = 'init',
  OUTGOING = 'outgoing_paid',
  PAID = 'paid',
  PROCESSING = 'processing',
  ARCHIVED = 'archived',
}

export interface ReconciliationDTO {
  accountNumber: string
  action: ReconciliationAction
  automatic: boolean
  bankStatementTransactionId: string
  clientCompanyName: string
  clientDebtorId: string
  clientId: string
  comment: string
  createdAt: Date
  createdBy: string
  currency: Currency
  debtorCompanyName: string
  fullReturnId: string
  fullReturnNoteNumber: string
  id: string
  invoiceAmount: number
  invoiceId: string
  invoiceNumber: string
  invoiceOpenAmount: number
  negativeWriteOff: number
  noteId: string
  noteNumber: string
  noteType: NoteType
  paymentDate: Date
  paymentReceivedFrom: CompanyType
  productType: ReconciliationLogProductType
  reconcileCompanyName: string
  reconciledAmount: number
  reconciliation: ReconciliationType
  repaymentFor: RepaymentForType
  rpaNumber: string
  sourcingAgentAdministrates: string
  sourcingAgentName: string
  status: ReconciliationLogStatus
  vatInvoiceId: string
  vatNoteNumber: string
  partialReturnNoteNumber: string
  partialReturnProductId: string
  returnTo: ReconciliationPaymentReturnTo
  returnToIban: string
  returnReason: string
  returnSettlementAmount: number
  returnStatus: ReconciliationPaymentReturnStatus
  investorId: string
  investorName: string
  investigationReason: ReconciliationInvestigationReason
  bankReference: string
  senderCompanyName: string
}

export enum ReconciliationsTableAssessor {
  RECONCILIATION = 'reconciliation',
  REPAYMENT_FOR = 'repaymentFor',
  PRODUCT = 'productType',
  STATUS = 'status',
  SOURCING_AGENT = 'sourcingAgentName',
  SOURCING_AGENT_ADMINISTRATES = 'sourcingAgentAdministrate',
  PAYMENT_RECEIVED_FROM = 'paymentReceivedFrom',
  INVESTOR = 'investorName',
  COMPANY = 'reconcileCompanyName',
  INVOICE_CLIENT_COMPANY = 'clientCompanyName',
  INVOICE_DEBTOR_COMPANY = 'debtorCompanyName',
  NUMBER = 'number',
  AMOUNT = 'invoiceAmount',
  OPEN_AMOUNT = 'invoiceOpenAmount',
  NOTE_NUMBER = 'noteNumber',
  RECONCILED_AMOUNT = 'reconciledAmount',
  NEGATIVE_WRITE_OFF = 'negativeWriteOff',
  PAYMENT_DATE = 'paymentDate',
  CREATED_BY = 'createdBy',
  CREATED_AT = 'createdAt',
  RECONCILIATION_TYPE = 'action',
  GL_ACCOUNT = 'accountNumber',
  COMMENT = 'comment',
  RETURN_TO = 'returnTo',
  RETURN_TO_IBAN = 'returnToIban',
  RETURN_AMOUNT = 'returnSettlementAmount',
  RETURN_REASON = 'returnReason',
  BANK_REFERENCE = 'bankReference',
  SENDER_COMPANY_NAME = 'senderCompanyName',
  INVESTIGATION_REASON = 'investigationReason',
  ACTIONS = 'actions',
}

export interface ReconciliationDetailsDTO {
  importDetails: BankStatementDTO
  paymentDetails: BankStatementTransactionDTO
  reconciliations: Array<ReconciliationDTO>
}

export interface BankTransactionDetailsData {
  reconciliationDetails: ReconciliationDetailsDTO
}

export interface BankTransactionDetailsVariables {
  bankTransactionId: string
}

export interface ArchiveTransactionData {
  archiveReconciliation: ReconciliationDTO
}

export interface ArchiveTransactionVariables {
  reconciliationId: string
}

export interface ReturnPaymentDTO {
  amount: number
  createdOn: Date
  id: string
  returnPaymentId: string
  returnPaymentDetails: string
  returnReason: string
  returnStatus: ReconciliationPaymentReturnStatus
  returnTo: ReconciliationPaymentReturnTo
  returnToCompanyName: string
  returnToIban: string
  sourcingAgentAdministrates: string
  sourcingAgentName: string
  currency: Currency
  productType: PayoutProductType
}

export interface ReturnPaymentTotalDTO {
  amount: number
}

export interface PageWithTotals_ReturnPaymentDTO_ReturnPaymentTotalDTO {
  content: Array<ReturnPaymentDTO>
  number: number
  size: number
  totalElements: number
  totalPages: number
  totals: ReturnPaymentTotalDTO
}

export interface ReturnPaymentsData {
  returnPayments: PageWithTotals_ReturnPaymentDTO_ReturnPaymentTotalDTO
}

export interface ReturnPaymentsVariables {
  amount?: MinMaxFilterInput
  ibanFrom?: string
  returnPaymentId?: string
  returnPaymentDetails?: string
  returnStatus?: ReconciliationPaymentReturnStatus[]
  returnToCompanyName?: string
  returnTo?: ReconciliationPaymentReturnTo[]
  returnToIban?: string
  sourcingAgentAdministrates?: AdministrationTypes[]
  createdOn?: FromToFilterInput
  sortDirection: Direction
  sortField: string
  pageSize: number
  pageNum: number
}

export interface MarkAsPaidReturnPaymentData {
  markAsPaidReturnPayments: ReturnPaymentDTO
}

export interface MarkAsPaidReturnPaymentVariables {
  id: string
}

export interface BlockReturnPaymentData {
  blockReturnPayments: ReturnPaymentDTO
}

export interface BlockReturnPaymentVariables {
  id: string
}

export interface UnblockReturnPaymentData {
  unblockReturnPayments: ReturnPaymentDTO
}

export interface UnblockReturnPaymentVariables {
  id: string
}

export enum InvestorWithdrawalPaymentStatus {
  ARCHIVED = 'archived',
  PAID = 'paid',
  PROCESSING = 'processing',
  NEW = 'init',
  BLOCKED = 'blocked',
}

export interface InvestmentAccruedInterestDetailsDTO {
  balance: number
  dailyInterest: number
  days: number
  from: Date
  interest: number
  to: Date
}

export interface InterestPayoutDTO {
  amount: number
  createdOn: Date
  id: string
  investmentPlanId: string
  interestPaymentEndDate: Date
  interestPaymentStartDate: Date
  investmentNumber: string
  investorsName: string
  status: InvestorWithdrawalPaymentStatus
  accruedInterestDetails: Array<InvestmentAccruedInterestDetailsDTO>
}

export interface Page_InterestPayoutDTO {
  content: Array<InterestPayoutDTO>
  number: number
  size: number
  totalElements: number
  totalPages: number
}

export interface InvestmentInterestPayoutsData {
  investmentInterestPayoutList: Page_InterestPayoutDTO
}

export interface InvestmentInterestPayoutsVariables {
  investmentNumber?: string
  amount?: MinMaxFilterInput
  investorsName?: string
  interestPaymentEndDate?: FromToFilterInput
  interestPaymentStartDate?: FromToFilterInput
  status?: InvestorWithdrawalPaymentStatus[]
  sortDirection: Direction
  sortField: string
  pageSize: number
  pageNum: number
}

export interface MarkAsPaidInterestPayoutData {
  markInterestPayoutAsPaid: InterestPayoutDTO
}

export interface MarkAsPaidInterestPayoutVariables {
  id: string
}

export interface BlockInterestPayoutData {
  blockInterestPayout: InterestPayoutDTO
}

export interface BlockInterestPayoutVariables {
  id: string
}

export interface UnblockInterestPayoutData {
  unblockInterestPayout: InterestPayoutDTO
}

export interface UnblockInterestPayoutVariables {
  id: string
}

export interface PayoutStatisticsDTO {
  totalAmount: number
  totalTransactions: number
}

export interface GenerateInvestmentInterestsToBePaidData {
  generateInvestmentInterestsToBePaid: PayoutStatisticsDTO
}

export interface GenerateInvestmentInterestsToBePaidVariables {
  period: string
}

export interface GenerateInvestmentInterestsToBePaidFormValues {
  period: string
}

export enum GenerateInvestmentInterestsToBePaidFieldNames {
  MONTH_PERIOD = 'period',
}

export interface ReconciliationInvestigationEmailDTO {
  companyName: string
  createdByEmail: string
  createdByName: string
  createdOn: Date
  emailTo: InvestigationCompanyType
  id: string
  language: string
  lastSent: Date
  paymentInvoiceNumbers: string
  recipients: string[]
  reconciliationId: string
}

export enum AuditActions {
  RECONCILIATION_INVESTIGATION_COMMENT_UPDATED = 'RECONCILIATION_INVESTIGATION_COMMENT_UPDATED',
  RECONCILIATION_INVESTIGATION_EMAIL_RESENT = 'RECONCILIATION_INVESTIGATION_EMAIL_RESENT',
  RECONCILIATION_INVESTIGATION_EMAIL_CREATED = 'RECONCILIATION_INVESTIGATION_EMAIL_CREATED',
}

export interface ReconciliationInvestigationAuditDTO {
  action: AuditActions
  comment: string
  createdAt: Date
  id: string
  userFullName: string
  investigationReason: ReconciliationInvestigationReason
  recipients: string[]
}

export interface InvestigationGeneralLedgerAccountDetailsDTO {
  accountNumber: string
  comment: string
  emails: Array<ReconciliationInvestigationEmailDTO>
  investigationReason: ReconciliationInvestigationReason
  logs: Array<ReconciliationInvestigationAuditDTO>
  reconciliationId: string
}

export interface ReconciliationInvestigationDetailsData {
  reconciliationInvestigationDetails: InvestigationGeneralLedgerAccountDetailsDTO
}

export interface ReconciliationInvestigationDetailsVariables {
  reconciliationId: string
}

export interface UpdateReconciliationInvestigationCommentData {
  updateReconciliationInvestigationComment: InvestigationGeneralLedgerAccountDetailsDTO
}

export interface UpdateReconciliationInvestigationCommentVariables {
  comment: string
  reconciliationId: string
}

export interface UpdateReconciliationInvestigationCommentFormValues {
  comment: string
}

export enum UpdateReconciliationInvestigationCommentFieldsNames {
  COMMENT = 'comment',
}

export interface CreateReconciliationInvestigationEmailData {
  createReconciliationInvestigationEmail: InvestigationGeneralLedgerAccountDetailsDTO
}

export interface CreateReconciliationInvestigationEmailVariables {
  investigationEmailLanguage: string
  investigationInvoiceNumbers: string
  investigationEmailRecipients: string[]
  investigationEmailTo: InvestigationCompanyType
  investigationReason: ReconciliationInvestigationReason
  reconciliationId: string
  investigationCompanyId: string
}

export interface CreateReconciliationInvestigationEmailFormValues {
  investigationEmailLanguage: string
  investigationInvoiceNumbers: string
  investigationEmailRecipients: string[]
  investigationEmailTo: InvestigationCompanyType
  investigationReason: ReconciliationInvestigationReason
  investigationCompanyId: string
}

export enum CreateReconciliationInvestigationEmailFieldsNames {
  INVESTIGATION_REASON = 'investigationReason',
  INVESTIGATION_EMAIL_TO = 'investigationEmailTo',
  INVESTIGATION_COMPANY_ID = 'investigationCompanyId',
  INVESTIGATION_EMAIL_RECIPIENTS = 'investigationEmailRecipients',
  INVESTIGATION_EMAIL_LANGUAGE = 'investigationEmailLanguage',
  INVESTIGATION_INVOICE_NUMBERS = 'investigationInvoiceNumbers',
}

export interface ResendReconciliationInvestigationEmailData {
  resendReconciliationInvestigationEmail: InvestigationGeneralLedgerAccountDetailsDTO
}

export interface ResendReconciliationInvestigationEmailVariables {
  investigationEmailId: string
}

export interface DownloadInvestigationEmailElmFileData {
  downloadReconciliationInvestigationCmpEmailElmFile: ReportFileDTO
}

export interface DownloadInvestigationEmailElmFileVariables {
  investigationEmailId: string
}
